import React, { useEffect, useState } from 'react';
import config from '../config';
import {
  CategoryDynamicComponentAssignment as CategoryDynamicComponent,
  DynamicComponentTemplate as DynamicComponent,
} from '../types/dynamicComponents';
import {
  getDefaultSquaresConfigJson,
  getDefaultMultiplicationConfigJson,
  MULTIPLICATION_TABLE_COMPONENT_TYPE,
  SQUARES_TABLE_COMPONENT_TYPE,
} from '../components/dynamic/definitions';

interface Category {
  id: number;
  name: string;
  subCategories?: Category[];
}

const placementOptions = [
  { value: 1, label: 'After Overview' },
  { value: 2, label: 'After Tips' },
  { value: 3, label: 'Before Examples' },
  { value: 4, label: 'After Examples' },
  { value: 5, label: 'End Of Chapter' },
];

const defaultMultiplicationConfig = getDefaultMultiplicationConfigJson();
const defaultSquaresConfig = getDefaultSquaresConfigJson();

const applyDefaultConfigForComponentType = (componentType: string): string => {
  const normalizedType = componentType.trim().toLowerCase();
  if (normalizedType === MULTIPLICATION_TABLE_COMPONENT_TYPE) {
    return defaultMultiplicationConfig;
  }
  if (normalizedType === SQUARES_TABLE_COMPONENT_TYPE) {
    return defaultSquaresConfig;
  }
  return '';
};

const ManageDynamicComponents: React.FC = () => {
  const [components, setComponents] = useState<DynamicComponent[]>([]);
  const [assignments, setAssignments] = useState<CategoryDynamicComponent[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [componentEditId, setComponentEditId] = useState<number | null>(null);
  const [componentIsAdding, setComponentIsAdding] = useState(false);
  const [componentFormData, setComponentFormData] = useState({
    id: 0,
    name: '',
    componentType: MULTIPLICATION_TABLE_COMPONENT_TYPE,
    configJson: defaultMultiplicationConfig,
    isEnabled: true,
  });

  const [assignmentEditId, setAssignmentEditId] = useState<number | null>(null);
  const [assignmentIsAdding, setAssignmentIsAdding] = useState(false);
  const [assignmentFormData, setAssignmentFormData] = useState({
    id: 0,
    categoryId: 0,
    dynamicComponentId: 0,
    placementType: 1,
    displayOrder: 1,
    introHtml: '',
    captionOverride: '',
    isEnabled: true,
  });

  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAll();
  }, []);

  const flattenCategories = (tree: Category[]): Category[] => {
    const flattened: Category[] = [];

    const walk = (nodes: Category[]) => {
      nodes.forEach((node) => {
        flattened.push({ id: node.id, name: node.name });
        if (node.subCategories && node.subCategories.length > 0) {
          walk(node.subCategories);
        }
      });
    };

    walk(tree);
    return flattened;
  };

  const loadAll = async () => {
    try {
      setLoading(true);

      const [componentsResp, assignmentsResp, categoriesResp] = await Promise.all([
        fetch(`${config.apiBaseUrl}/api/dynamiccomponents`),
        fetch(`${config.apiBaseUrl}/api/categorydynamiccomponents`),
        fetch(`${config.apiBaseUrl}/api/categories`),
      ]);

      if (!componentsResp.ok) throw new Error('Failed to load dynamic components');
      if (!assignmentsResp.ok) throw new Error('Failed to load category assignments');
      if (!categoriesResp.ok) throw new Error('Failed to load categories');

      const componentsData: DynamicComponent[] = await componentsResp.json();
      const assignmentsData: CategoryDynamicComponent[] = await assignmentsResp.json();
      const categoriesTree: Category[] = await categoriesResp.json();
      const categoriesData = flattenCategories(categoriesTree);

      setComponents(componentsData);
      setAssignments(assignmentsData);
      setCategories(categoriesData);

      if (!componentIsAdding && componentEditId === null && componentsData.length > 0) {
        handleSelectComponent(componentsData[0]);
      }

      if (!assignmentIsAdding && assignmentEditId === null && assignmentsData.length > 0) {
        handleSelectAssignment(assignmentsData[0]);
      }

      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectComponent = (component: DynamicComponent) => {
    setComponentFormData({
      id: component.id,
      name: component.name,
      componentType: component.componentType,
      configJson: component.configJson || '',
      isEnabled: component.isEnabled,
    });
    setComponentEditId(component.id);
    setComponentIsAdding(false);
  };

  const handleAddComponent = () => {
    setComponentFormData({
      id: 0,
      name: '',
      componentType: MULTIPLICATION_TABLE_COMPONENT_TYPE,
      configJson: defaultMultiplicationConfig,
      isEnabled: true,
    });
    setComponentEditId(null);
    setComponentIsAdding(true);
  };

  const handleComponentTypeChange = (componentType: string) => {
    setComponentFormData((previous) => ({
      ...previous,
      componentType,
      configJson: applyDefaultConfigForComponentType(componentType),
    }));
  };

  const handleSaveComponent = async () => {
    if (!componentFormData.name.trim()) {
      setError('Component name is required');
      return;
    }

    if (!componentFormData.componentType.trim()) {
      setError('Component type is required');
      return;
    }

    if (componentFormData.configJson.trim()) {
      try {
        JSON.parse(componentFormData.configJson);
      } catch {
        setError('Component config must be valid JSON');
        return;
      }
    }

    try {
      const payload = {
        name: componentFormData.name.trim(),
        componentType: componentFormData.componentType.trim(),
        configJson: componentFormData.configJson.trim() || null,
        isEnabled: componentFormData.isEnabled,
      };

      const shouldCreate = componentIsAdding || componentFormData.id <= 0;

      const url = shouldCreate
        ? `${config.apiBaseUrl}/api/dynamiccomponents`
        : `${config.apiBaseUrl}/api/dynamiccomponents/${componentFormData.id}`;

      const method = shouldCreate ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const message = await response.text();
        let parsedMessage = message;
        try {
          const parsed = JSON.parse(message);
          if (parsed?.message) {
            parsedMessage = parsed.message;
          }
        } catch {
        }
        throw new Error(parsedMessage || 'Failed to save dynamic component');
      }

      await loadAll();
      setComponentIsAdding(false);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to save dynamic component');
    }
  };

  const handleDeleteComponent = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this component template?')) return;

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/dynamiccomponents/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete dynamic component');

      await loadAll();
      if (componentEditId === id) {
        setComponentEditId(null);
        setComponentIsAdding(false);
      }
      setError(null);
    } catch {
      setError('Failed to delete dynamic component');
    }
  };

  const handleSelectAssignment = (item: CategoryDynamicComponent) => {
    setAssignmentFormData({
      id: item.id,
      categoryId: item.categoryId,
      dynamicComponentId: item.dynamicComponentId,
      placementType: item.placementType,
      displayOrder: item.displayOrder,
      introHtml: item.introHtml || '',
      captionOverride: item.captionOverride || '',
      isEnabled: item.isEnabled,
    });
    setAssignmentEditId(item.id);
    setAssignmentIsAdding(false);
  };

  const handleAddAssignment = () => {
    setAssignmentFormData({
      id: 0,
      categoryId: categories.length > 0 ? categories[0].id : 0,
      dynamicComponentId: components.length > 0 ? components[0].id : 0,
      placementType: 1,
      displayOrder: 1,
      introHtml: '',
      captionOverride: '',
      isEnabled: true,
    });
    setAssignmentEditId(null);
    setAssignmentIsAdding(true);
  };

  const handleSaveAssignment = async () => {
    if (!assignmentFormData.categoryId) {
      setError('Category is required');
      return;
    }

    if (!assignmentFormData.dynamicComponentId) {
      setError('Dynamic component is required');
      return;
    }

    if (!assignmentFormData.displayOrder || assignmentFormData.displayOrder <= 0) {
      setError('Display order must be greater than 0');
      return;
    }

    try {
      const payload = {
        categoryId: assignmentFormData.categoryId,
        dynamicComponentId: assignmentFormData.dynamicComponentId,
        placementType: assignmentFormData.placementType,
        displayOrder: assignmentFormData.displayOrder,
        introHtml: assignmentFormData.introHtml || null,
        captionOverride: assignmentFormData.captionOverride || null,
        isEnabled: assignmentFormData.isEnabled,
      };

      const shouldCreate = assignmentIsAdding || assignmentFormData.id <= 0;

      const url = shouldCreate
        ? `${config.apiBaseUrl}/api/categorydynamiccomponents`
        : `${config.apiBaseUrl}/api/categorydynamiccomponents/${assignmentFormData.id}`;

      const method = shouldCreate ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const message = await response.text();
        let parsedMessage = message;
        try {
          const parsed = JSON.parse(message);
          if (parsed?.message) {
            parsedMessage = parsed.message;
          }
        } catch {
        }
        throw new Error(parsedMessage || 'Failed to save assignment');
      }

      await loadAll();
      setAssignmentIsAdding(false);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to save assignment');
    }
  };

  const handleDeleteAssignment = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/categorydynamiccomponents/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete assignment');

      await loadAll();
      if (assignmentEditId === id) {
        setAssignmentEditId(null);
        setAssignmentIsAdding(false);
      }
      setError(null);
    } catch {
      setError('Failed to delete assignment');
    }
  };

  const getPlacementLabel = (value: number) => {
    return placementOptions.find((x) => x.value === value)?.label || `Unknown (${value})`;
  };

  return (
    <div
      style={{
        maxWidth: 1440,
        margin: '0 auto',
        padding: isMobile ? '16px 8px' : isTablet ? '20px 12px' : '24px 20px',
        textAlign: 'left',
      }}
    >
      <h1
        style={{
          fontSize: isMobile ? 24 : isTablet ? 28 : 32,
          fontWeight: 700,
          marginBottom: isMobile ? 16 : 24,
          color: '#222',
        }}
      >
        Manage Dynamic Components
      </h1>

      {error && (
        <div
          style={{
            background: '#ffebee',
            color: '#c62828',
            padding: isMobile ? 12 : 16,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile || isTablet ? '1fr' : '1fr 1fr', gap: 24 }}>
        <div
          style={{
            backgroundColor: '#f5f5f5',
            border: '2px solid #2196f3',
            borderRadius: 8,
            padding: isMobile ? 12 : 16,
          }}
        >
          <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, margin: '0 0 12px 0', color: '#222' }}>
            Component Templates
          </h2>

          <button
            onClick={handleAddComponent}
            style={{
              marginBottom: 16,
              padding: isMobile ? '10px 16px' : '10px 18px',
              backgroundColor: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            ➕ Add Template
          </button>

          {loading ? (
            <div style={{ color: '#666', padding: 12 }}>Loading templates...</div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              {components.map((component) => (
                <div
                  key={component.id}
                  onClick={() => handleSelectComponent(component)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: 10,
                    marginBottom: 8,
                    borderRadius: 6,
                    border: `2px solid ${componentEditId === component.id ? '#2196f3' : '#94a3b8'}`,
                    backgroundColor: componentEditId === component.id ? '#e3f2fd' : '#f8fafc',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#222', fontSize: 14 }}>{component.name}</div>
                    <div style={{ color: '#666', fontSize: 12 }}>{component.componentType}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteComponent(component.id);
                    }}
                    style={{
                      backgroundColor: '#f44336',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      padding: '6px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {components.length === 0 && <div style={{ color: '#777', fontSize: 13 }}>No component templates yet.</div>}
            </div>
          )}

          <div style={{ backgroundColor: '#fff', borderRadius: 6, border: '1px solid #ddd', padding: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 10, color: '#222' }}>
              {componentIsAdding ? 'Add Template' : 'Edit Template'}
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Name</label>
              <input
                type="text"
                value={componentFormData.name}
                onChange={(e) => setComponentFormData({ ...componentFormData, name: e.target.value })}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Component Type</label>
              <select
                value={componentFormData.componentType}
                onChange={(e) => handleComponentTypeChange(e.target.value)}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              >
                <option value={MULTIPLICATION_TABLE_COMPONENT_TYPE}>multiplication_table</option>
                <option value={SQUARES_TABLE_COMPONENT_TYPE}>squares_table</option>
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Config JSON</label>
              <textarea
                value={componentFormData.configJson}
                onChange={(e) => setComponentFormData({ ...componentFormData, configJson: e.target.value })}
                rows={8}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#333', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={componentFormData.isEnabled}
                  onChange={(e) => setComponentFormData({ ...componentFormData, isEnabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>

            <button
              onClick={handleSaveComponent}
              style={{
                padding: '9px 16px',
                backgroundColor: '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              💾 Save Template
            </button>
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#f5f5f5',
            border: '2px solid #2196f3',
            borderRadius: 8,
            padding: isMobile ? 12 : 16,
          }}
        >
          <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, margin: '0 0 12px 0', color: '#222' }}>
            Category Assignments
          </h2>

          <button
            onClick={handleAddAssignment}
            style={{
              marginBottom: 16,
              padding: isMobile ? '10px 16px' : '10px 18px',
              backgroundColor: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            ➕ Add Assignment
          </button>

          {loading ? (
            <div style={{ color: '#666', padding: 12 }}>Loading assignments...</div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              {assignments.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelectAssignment(item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: 10,
                    marginBottom: 8,
                    borderRadius: 6,
                    border: `2px solid ${assignmentEditId === item.id ? '#2196f3' : '#94a3b8'}`,
                    backgroundColor: assignmentEditId === item.id ? '#e3f2fd' : '#f8fafc',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#222', fontSize: 14 }}>
                      {item.categoryName} → {item.dynamicComponentName}
                    </div>
                    <div style={{ color: '#666', fontSize: 12 }}>
                      {getPlacementLabel(item.placementType)} • Order {item.displayOrder}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAssignment(item.id);
                    }}
                    style={{
                      backgroundColor: '#f44336',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      padding: '6px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {assignments.length === 0 && <div style={{ color: '#777', fontSize: 13 }}>No assignments yet.</div>}
            </div>
          )}

          <div style={{ backgroundColor: '#fff', borderRadius: 6, border: '1px solid #ddd', padding: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 10, color: '#222' }}>
              {assignmentIsAdding ? 'Add Assignment' : 'Edit Assignment'}
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Category</label>
              <select
                value={assignmentFormData.categoryId}
                onChange={(e) => setAssignmentFormData({ ...assignmentFormData, categoryId: Number(e.target.value) })}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              >
                <option value={0}>Select category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Dynamic Component</label>
              <select
                value={assignmentFormData.dynamicComponentId}
                onChange={(e) => setAssignmentFormData({ ...assignmentFormData, dynamicComponentId: Number(e.target.value) })}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              >
                <option value={0}>Select component</option>
                {components.map((component) => (
                  <option key={component.id} value={component.id}>
                    {component.name} ({component.componentType})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Placement Type</label>
              <select
                value={assignmentFormData.placementType}
                onChange={(e) => setAssignmentFormData({ ...assignmentFormData, placementType: Number(e.target.value) })}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              >
                {placementOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Display Order</label>
              <input
                type="number"
                min={1}
                value={assignmentFormData.displayOrder}
                onChange={(e) => setAssignmentFormData({ ...assignmentFormData, displayOrder: Number(e.target.value) || 1 })}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Caption Override</label>
              <input
                type="text"
                value={assignmentFormData.captionOverride}
                onChange={(e) => setAssignmentFormData({ ...assignmentFormData, captionOverride: e.target.value })}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Intro HTML</label>
              <textarea
                value={assignmentFormData.introHtml}
                onChange={(e) => setAssignmentFormData({ ...assignmentFormData, introHtml: e.target.value })}
                rows={4}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#333', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={assignmentFormData.isEnabled}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, isEnabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>

            <button
              onClick={handleSaveAssignment}
              style={{
                padding: '9px 16px',
                backgroundColor: '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              💾 Save Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageDynamicComponents;